import { useCallback, useEffect, useState } from 'react'

declare global {
    interface Window {
        CCM?: { acceptedEmbeddings: { name: string; id: string }[]; openControlPanel: () => void }
    }
}

export const SERVICES_NEEDING_CONSENT = {
    GOOGLE_MAPS: 'Google Maps',
    DATADOG: 'Datadog Real User Monitoring',
}

const hasCookieConsentScript = () => document.querySelector('script[src*="cloud.ccm19.de/app.js"]') instanceof HTMLScriptElement

const isCookieConsentRequired = () => 'CCM' in window || hasCookieConsentScript()

const getServicesWithConsent = () => window.CCM?.acceptedEmbeddings.map((embedding) => embedding.name) || []

const isCookieConsentGiven = (name: string) => {
    const servicesWithConsent = getServicesWithConsent()
    return servicesWithConsent.includes(name)
}

export const useCookieConsent = (name: string) => {
    const isInitialDisabled = isCookieConsentRequired() && !isCookieConsentGiven(name)
    const [isDisabled, setIsDisabled] = useState(isInitialDisabled)

    useEffect(() => {
        const listener = () => setIsDisabled(!isCookieConsentGiven(name))
        addEventListener('ccm19EmbeddingAccepted', listener)
        addEventListener('ccm19WidgetClosed', listener)

        return () => {
            removeEventListener('ccm19EmbeddingAccepted', listener)
            removeEventListener('ccm19WidgetClosed', listener)
        }
    }, [name])

    return { isEnabled: !isDisabled }
}

export const useCookieConsentInit = () => {
    useReloadOnCookieConsentRevoked()
}

const useReloadOnCookieConsentRevoked = () => {
    const initialConsents = getServicesWithConsent()
    const [prevConsents, setPrevConsents] = useState<string[]>(initialConsents)

    const reloadOnConsentRevoked = useCallback(() => {
        const currConsents = getServicesWithConsent()
        const hasAnyConsentBeenRevoked = prevConsents?.some((prevConsent) => !currConsents.includes(prevConsent))
        if (hasAnyConsentBeenRevoked) {
            location.reload()
        } else {
            setPrevConsents(currConsents)
        }
    }, [prevConsents])

    useEffect(() => {
        addEventListener('ccm19EmbeddingAccepted', reloadOnConsentRevoked)
        addEventListener('ccm19WidgetClosed', reloadOnConsentRevoked)
        return () => {
            removeEventListener('ccm19EmbeddingAccepted', reloadOnConsentRevoked)
            removeEventListener('ccm19WidgetClosed', reloadOnConsentRevoked)
        }
    }, [reloadOnConsentRevoked])
}
